import React, { Fragment, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import { McTable } from "@maersk-global/mds-react-wrapper/components-core/mc-table";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McModal } from "@maersk-global/mds-react-wrapper/components-core/mc-modal";
import { McTextarea } from "@maersk-global/mds-react-wrapper/components-core/mc-textarea";
import { McPopover } from "@maersk-global/mds-react-wrapper/components-core/mc-popover";
import { McCheckbox } from "@maersk-global/mds-react-wrapper/components-core/mc-checkbox";
import { McTooltip } from "@maersk-global/mds-react-wrapper";
import AddChangeRestrictionModal from "./AddChangeRestrictionModal";

const changeRestrictionColumns = [
  { id: "action", label: "Actions", sortDisabled: true, width: "120px" },
  { id: "app_id", label: "Application ID" },
  { id: "app_name", label: "App Instance Name" },
  { id: "platform", label: "Platform" },
  { id: "portfolio", label: "Portfolio" },
  {
    id: "added_to_change_restriction",
    label: "Added to Change Restriction",
  },
  { id: "added_by", label: "Added by" },
  {
    id: "reason_for_restriction",
    label: "Reason for restriction",
    sortDisabled: true,
  },
];

const mapRestrictionRecord = (item, idx) => {
  const addedDateSource =
    item.added_to_change_restriction ||
    item.added_on ||
    item.created_at ||
    item.created_date ||
    null;

  let formattedAddedDate = "-";
  if (addedDateSource) {
    const parsedDate = DateTime.fromISO(String(addedDateSource));
    formattedAddedDate = parsedDate.isValid
      ? parsedDate.toFormat("dd-MMM-yyyy")
      : String(addedDateSource);
  }

  return {
    id:
      item.id ||
      item.restriction_id ||
      `${item.app_id || "record"}-${item.app_name || "name"}-${idx}`,
    app_id: item.app_id || "-",
    app_name: item.app_name || "-",
    platform: item.platform || "-",
    portfolio: item.portfolio || "-",
    added_to_change_restriction: formattedAddedDate,
    added_by: item.added_by || item.created_by || "-",
    reason_for_restriction:
      item.reason_for_restriction || item.reason || item.comments || "-",
    _raw: item,
  };
};

export default function ChangeRestrictionSection({ tokenFetched, graphData, userRole }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSubmittingAdd, setIsSubmittingAdd] = useState(false);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [recordToEdit, setRecordToEdit] = useState(null);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [deleteComment, setDeleteComment] = useState("");
  const [columnFilters, setColumnFilters] = useState({});
  const [sortColumn, setSortColumn] = useState("added_to_change_restriction");
  const [sortDirection, setSortDirection] = useState("descending");

  const normalizedUserRole = String(userRole || "").toLowerCase();
  const canManageRestrictions =
    normalizedUserRole === "change manager" || normalizedUserRole === "admin";

  const getUniqueColumnValues = (data, columnId) => {
    if (!data || data.length === 0) {
      return [];
    }

    const values = [];
    let hasBlanks = false;

    data.forEach((item) => {
      const value = item[columnId];
      if (
        value === null ||
        value === undefined ||
        value === "" ||
        value === "-" ||
        value === " "
      ) {
        hasBlanks = true;
      } else {
        values.push(String(value));
      }
    });

    const uniqueValues = [...new Set(values)].sort();
    if (hasBlanks) {
      uniqueValues.push("[Blanks]");
    }

    return uniqueValues;
  };

  const applyColumnFilters = (data, filters) => {
    if (Object.keys(filters).length === 0) {
      return data;
    }

    return data.filter((item) => {
      return Object.entries(filters).every(([columnId, selectedValues]) => {
        if (!Array.isArray(selectedValues) || selectedValues.length === 0) {
          return true;
        }

        const itemValue = item[columnId];
        const matchValue =
          itemValue === null ||
          itemValue === undefined ||
          itemValue === "" ||
          itemValue === "-" ||
          itemValue === " "
            ? "[Blanks]"
            : String(itemValue);

        return selectedValues.includes(matchValue);
      });
    });
  };

  const handleColumnFilterChange = (columnId, selectedValues) => {
    setColumnFilters((prev) => {
      const newFilters = { ...prev };
      const valuesArray = Array.isArray(selectedValues) ? selectedValues : [];

      if (valuesArray.length === 0) {
        delete newFilters[columnId];
      } else {
        newFilters[columnId] = valuesArray;
      }

      return newFilters;
    });
  };

  const renderColumnFilter = (columnId, label, dataKey, data) => {
    const uniqueValues = getUniqueColumnValues(data, dataKey);
    const selectedValues = columnFilters[dataKey] || [];
    const isFiltered = selectedValues.length > 0;

    return (
      <div
        slot={`${columnId}_header`}
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "4px",
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <span>{label}</span>
        <McPopover
          position="bottom-right"
          trigger="click"
          click={(event) => event.stopPropagation()}
        >
          <McButton
            appearance="neutral"
            fit="small"
            hiddenlabel={true}
            icon={isFiltered ? "funnel-solid" : "funnel"}
            label="Show filter menu"
            padding="compact"
            slot="trigger"
            variant="plain"
            click={(event) => event.stopPropagation()}
          />
          <div
            style={{ padding: "16px 12px", maxHeight: "300px", overflowY: "auto" }}
            onClick={(event) => event.stopPropagation()}
          >
            {uniqueValues.map((value, index) => (
              <McCheckbox
                key={`${columnId}-${index}`}
                label={value}
                value={value}
                checked={selectedValues.includes(value)}
                change={(event) => {
                  event.stopPropagation();
                  const isChecked = event.detail;
                  const newSelectedValues = isChecked
                    ? [...selectedValues, value]
                    : selectedValues.filter((item) => item !== value);
                  handleColumnFilterChange(dataKey, newSelectedValues);
                }}
                style={{ display: "block", marginBottom: "8px" }}
              />
            ))}
          </div>
        </McPopover>
      </div>
    );
  };

  const filteredRecords = useMemo(
    () => applyColumnFilters(records, columnFilters),
    [records, columnFilters],
  );

  const sortedFilteredRecords = useMemo(() => {
    const rows = [...filteredRecords];

    rows.sort((a, b) => {
      // Keep actions column unsortable.
      if (sortColumn === "action") {
        return 0;
      }

      let valueA = a?.[sortColumn];
      let valueB = b?.[sortColumn];

      if (sortColumn === "added_to_change_restriction") {
        const parsedA = DateTime.fromFormat(String(valueA || ""), "dd-MMM-yyyy");
        const parsedB = DateTime.fromFormat(String(valueB || ""), "dd-MMM-yyyy");
        valueA = parsedA.isValid ? parsedA.toMillis() : -Infinity;
        valueB = parsedB.isValid ? parsedB.toMillis() : -Infinity;
      }

      if (valueA === null || valueA === undefined) valueA = "";
      if (valueB === null || valueB === undefined) valueB = "";

      if (typeof valueA === "number" && typeof valueB === "number") {
        return sortDirection === "ascending" ? valueA - valueB : valueB - valueA;
      }

      const stringA = String(valueA).toLowerCase();
      const stringB = String(valueB).toLowerCase();

      if (stringA < stringB) return sortDirection === "ascending" ? -1 : 1;
      if (stringA > stringB) return sortDirection === "ascending" ? 1 : -1;
      return 0;
    });

    return rows;
  }, [filteredRecords, sortColumn, sortDirection]);

  const handleTableSorted = ({ detail: { column, direction } }) => {
    if (column === "action") {
      return;
    }
    setSortColumn(column);
    setSortDirection(direction);
  };

  const uniqueAppNamesCount = useMemo(() => {
    const uniqueAppNames = new Set(
      sortedFilteredRecords.map((record) => String(record.app_name || "").trim()),
    );
    uniqueAppNames.delete("");
    uniqueAppNames.delete("-");
    return uniqueAppNames.size;
  }, [sortedFilteredRecords]);

  const handleDownload = () => {
    const rows = sortedFilteredRecords.map((item) => ({
      "Application ID": item.app_id,
      "App Instance Name": item.app_name,
      Platform: item.platform,
      Portfolio: item.portfolio,
      "Added to Change Restriction": item.added_to_change_restriction,
      "Added by": item.added_by,
      "Reason for restriction": item.reason_for_restriction,
    }));

    if (rows.length === 0) {
      toast.info("No data available to download", {
        position: "top-center",
        autoClose: 2500,
      });
      return;
    }

    const headers = Object.keys(rows[0]);
    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        headers
          .map((header) => {
            const value = row[header] ?? "";
            const safeValue = String(value).replace(/"/g, '""');
            return `"${safeValue}"`;
          })
          .join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `Change_Restriction_${DateTime.now().toFormat("yyyy-MM-dd_HH-mm-ss")}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const fetchChangeRestrictions = async () => {
    if (!tokenFetched) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URI}/api/control_tower/change_restrictions`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt_token") || ""}`,
          },
        },
      );

      const rawRows = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];

      setRecords(rawRows.map(mapRestrictionRecord));
    } catch (error) {
      setRecords([]);
      toast.error("Failed to load change restriction data", {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChangeRestrictions();
  }, [tokenFetched]);

  const handleOpenAddModal = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    if (isSubmittingAdd) {
      return;
    }
    setIsAddModalOpen(false);
  };

  const handleOpenEditModal = (row) => {
    if (!canManageRestrictions) {
      return;
    }

    setRecordToEdit(row);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    if (isSubmittingEdit) {
      return;
    }

    setIsEditModalOpen(false);
    setRecordToEdit(null);
  };

  const handleSubmitAdd = async (payload) => {
    setIsSubmittingAdd(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URI}/api/control_tower/change_restrictions`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("jwt_token") || ""}`,
          },
        },
      );

      toast.success("Application added to change restriction", {
        position: "top-center",
        autoClose: 3000,
      });
      setIsAddModalOpen(false);
      await fetchChangeRestrictions();
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to add application to change restriction",
        {
          position: "top-center",
          autoClose: 3000,
        },
      );
    } finally {
      setIsSubmittingAdd(false);
    }
  };

  const handleSubmitEdit = async (payload) => {
    if (!recordToEdit) {
      return;
    }

    setIsSubmittingEdit(true);

    const targetId = recordToEdit?._raw?.id || recordToEdit?._raw?.restriction_id;

    try {
      if (targetId) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URI}/api/control_tower/change_restrictions/${encodeURIComponent(targetId)}`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("jwt_token") || ""}`,
            },
          },
        );
      } else {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URI}/api/control_tower/change_restrictions`,
          {
            ...payload,
            original_app_id: recordToEdit.app_id,
            original_app_name: recordToEdit.app_name,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("jwt_token") || ""}`,
            },
          },
        );
      }

      toast.success("Change restriction updated", {
        position: "top-center",
        autoClose: 3000,
      });
      setIsEditModalOpen(false);
      setRecordToEdit(null);
      await fetchChangeRestrictions();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update change restriction",
        {
          position: "top-center",
          autoClose: 3000,
        },
      );
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  const handleOpenDeleteModal = (row) => {
    if (!canManageRestrictions) {
      return;
    }

    if (!row) {
      return;
    }

    setRecordToDelete(row);
    setDeleteComment("");
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    if (isDeleting) {
      return;
    }

    setIsDeleteModalOpen(false);
    setDeleteComment("");
  };

  const handleDeleteRecord = async () => {
    if (!recordToDelete) {
      return;
    }

    setIsDeleting(true);

    const targetId = recordToDelete?._raw?.id || recordToDelete?._raw?.restriction_id;

    try {
      if (targetId) {
        await axios.delete(
          `${process.env.REACT_APP_BACKEND_URI}/api/control_tower/change_restrictions/${encodeURIComponent(targetId)}`,
          {
            data: {
              comments: deleteComment,
              deleted_by: graphData?.displayName || "",
            },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("jwt_token") || ""}`,
            },
          },
        );
      } else {
        await axios.delete(
          `${process.env.REACT_APP_BACKEND_URI}/api/control_tower/change_restrictions`,
          {
            data: {
              app_id: recordToDelete.app_id,
              app_name: recordToDelete.app_name,
              comments: deleteComment,
              deleted_by: graphData?.displayName || "",
            },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("jwt_token") || ""}`,
            },
          },
        );
      }

      toast.success("Change restriction deleted", {
        position: "top-center",
        autoClose: 3000,
      });
      setIsDeleteModalOpen(false);
      setRecordToDelete(null);
      setDeleteComment("");
      await fetchChangeRestrictions();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to delete change restriction",
        {
          position: "top-center",
          autoClose: 3000,
        },
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="dashboard-card">
      <div className="change-restriction-header">
        <h3 className="card-title" style={{fontWeight: '500', fontSize: '1.25rem'}}>Applications in Change Restriction</h3>
        <McButton
          appearance="primary"
          variant="filled"
          fit="medium"
          icon="plus"
          click={handleOpenAddModal}
          disabled={!canManageRestrictions}
        >
          Add to Change Restriction
        </McButton>
      </div>

      <div className="change-restriction-table-topbar">
        <span className="change-restriction-table-count">
          Total App Instances: {uniqueAppNamesCount}
        </span>
        <McButton
          label="Download"
          appearance="neutral"
          variant="plain"
          fit="small"
          icon="tray-arrow-down"
          click={handleDownload}
          disabled={loading}
        />
      </div>

      <McTable
        data={filteredRecords}
        columns={changeRestrictionColumns}
        datakey="id"
        fit="small"
        headersticky
        sortmanual
        sortchange={handleTableSorted}
        sortdefaultcolumnid={sortColumn}
        sortdefaultdirection={sortDirection}
        customstyles={`.mds-table { max-height: 46vh !important; }`}
        stateslotheight="140px"
      >

        {loading ? (
          <mc-loading-indicator slot="state"></mc-loading-indicator>
        ) : (
          sortedFilteredRecords.length === 0 && (
            <div slot="state" className="empty-row">
              No applications currently in change restriction.
            </div>
          )
        )}

        {renderColumnFilter("app_id", "Application ID", "app_id", records)}
        {renderColumnFilter("app_name", "App Instance Name", "app_name", records)}
        {renderColumnFilter("platform", "Platform", "platform", records)}
        {renderColumnFilter("portfolio", "Portfolio", "portfolio", records)}
        {renderColumnFilter("added_by", "Added by", "added_by", records)}

        {sortedFilteredRecords.map((row) => (
          <Fragment key={row.id}>
            <div slot={`${row.id}_action`}>
              <div className="change-restriction-action-buttons">
                <McTooltip
                  opendelay={50}
                  appearance="neutral-default"
                  trigger="hover focus"
                >
                  <McButton
                    slot="trigger"
                    fit="small"
                    click={() => handleOpenEditModal(row)}
                    icon="pencil"
                    hiddenlabel={true}
                    appearance="neutral"
                    variant="outlined"
                    disabled={!canManageRestrictions}
                  />
                  <span>Edit Details</span>
                </McTooltip>

                <McTooltip
                  opendelay={50}
                  appearance="neutral-default"
                  trigger="hover focus"
                >
                  <McButton
                    slot="trigger"
                    fit="small"
                    click={() => handleOpenDeleteModal(row)}
                    icon="trash"
                    hiddenlabel={true}
                    appearance="neutral"
                    variant="outlined"
                    disabled={!canManageRestrictions}
                  />
                  <span>Delete Record</span>
                </McTooltip>
              </div>
            </div>
            <div slot={`${row.id}_app_id`}>
              <span>{row.app_id}</span>
            </div>
            <div slot={`${row.id}_app_name`}>{row.app_name}</div>
            <div slot={`${row.id}_platform`}>{row.platform}</div>
            <div slot={`${row.id}_portfolio`}>{row.portfolio}</div>
            <div slot={`${row.id}_added_to_change_restriction`}>
              {row.added_to_change_restriction}
            </div>
            <div slot={`${row.id}_added_by`}>{row.added_by}</div>
            <div slot={`${row.id}_reason_for_restriction`}>
              {row.reason_for_restriction}
            </div>
          </Fragment>
        ))}
      </McTable>

      <AddChangeRestrictionModal
        isOpen={isAddModalOpen}
        isSubmitting={isSubmittingAdd}
        graphData={graphData}
        onClose={handleCloseAddModal}
        onSubmit={handleSubmitAdd}
      />

      <AddChangeRestrictionModal
        isOpen={isEditModalOpen}
        isSubmitting={isSubmittingEdit}
        graphData={graphData}
        isEditMode={true}
        initialData={recordToEdit?._raw || null}
        onClose={handleCloseEditModal}
        onSubmit={handleSubmitEdit}
      />

      <McModal
        heading="Delete Change Restriction Record?"
        dimension="medium"
        padding="default"
        open={isDeleteModalOpen}
        closed={handleCloseDeleteModal}
        closing={handleCloseDeleteModal}
        zindex={999}
      >
        <div className="change-restriction-delete-content">
          {recordToDelete && (
            <div className="change-restriction-delete-record-meta">
              <p>
                <strong>App Instance Name:</strong> {recordToDelete.app_name}
              </p>
              <p>
                <strong>Application ID:</strong> {recordToDelete.app_id}
              </p>
            </div>
          )}

          <div className="change-restriction-form-field">
            <label className="change-restriction-form-label">
              Comments (optional)
            </label>
            <McTextarea
              hiddenlabel={true}
              value={deleteComment}
              input={(event) => setDeleteComment(event.target.value)}
              placeholder="Enter comments for deletion"
              rows="4"
              fit="medium"
            />
          </div>
        </div>

        <McButton
          slot="secondaryAction"
          appearance="neutral"
          variant="outlined"
          dialogaction="cancel"
          fit="medium"
          disabled={isDeleting}
          click={handleCloseDeleteModal}
        >
          Cancel
        </McButton>
        <McButton
          slot="primaryAction"
          appearance="error"
          fit="medium"
          loading={isDeleting}
          disabled={isDeleting}
          click={handleDeleteRecord}
        >
          Delete
        </McButton>
      </McModal>
    </div>
  );
}
